import { useState } from "react";
import { Phone, Mail, Clock, MapPin, Send, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { submitContatto } from "@/lib/api";

const contactInfo = [
  {
    icon: Phone,
    title: "Telefono",
    value: "+39 327 282 3064",
    link: "tel:+393272823064",
    color: "#007AFF"
  },
  {
    icon: Mail,
    title: "Email",
    value: "info@finestreecolore.it",
    link: "mailto:info@finestreecolore.it",
    color: "#FF9500"
  },
  {
    icon: Clock,
    title: "Orari",
    value: "Lun - Ven: 09:00 - 18:00",
    subvalue: "Sabato e Domenica: Chiuso",
    color: "#4CD964"
  },
  {
    icon: Facebook,
    title: "Facebook",
    value: "Seguici su Facebook",
    link: "https://www.facebook.com/profile.php?id=61576471303622",
    color: "#1877F2"
  }
];

const EMPTY = { nome: "", email: "", telefono: "", messaggio: "", hp: "" };

export default function ContactSection() {
  const [formData, setFormData] = useState(EMPTY);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitContatto(formData);
      toast.success("Messaggio inviato! Ti risponderemo presto.");
      setFormData(EMPTY);
    } catch (error) {
      console.error("Error submitting contact:", error);
      toast.error(error.message || "Errore nell'invio. Riprova o contattaci direttamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contatti" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <p className="text-sm font-bold uppercase tracking-[0.2em] bg-clip-text text-transparent bg-gradient-to-r from-orange-500 via-blue-500 to-purple-500 mb-4">
            Contattaci
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0B1E36] mb-6">
            Siamo Qui per Te
          </h2>
          <p className="text-base md:text-lg text-[#64748B] leading-relaxed">
            Hai domande sui nostri prodotti o servizi? Contattaci e saremo felici di aiutarti.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {contactInfo.map((info) => (
                <div 
                  key={info.title}
                  className="bg-[#FAFAFA] rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
                  data-testid={`contact-info-${info.title.toLowerCase()}`}
                >
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${info.color}15` }}
                  >
                    <info.icon className="w-6 h-6" style={{ color: info.color }} />
                  </div>
                  <h4 className="font-semibold text-[#0B1E36] mb-2">{info.title}</h4>
                  {info.link ? (
                    <a 
                      href={info.link}
                      target={info.link.startsWith('http') ? '_blank' : undefined}
                      rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="text-[#64748B] hover:text-[#007AFF] transition-colors"
                    >
                      {info.value}
                    </a>
                  ) : (
                    <>
                      <p className="text-[#64748B]">{info.value}</p>
                      {info.subvalue && (
                        <p className="text-[#64748B] text-sm mt-1">{info.subvalue}</p>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Map placeholder or additional info */}
            <div className="bg-gradient-to-br from-[#0B1E36] to-[#1a3a5c] rounded-3xl p-8 text-white">
              <h3 className="text-xl font-bold mb-4">Perché Scegliere Noi?</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-[#4CD964] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-white/90">Esperienza pluriennale nel settore</span>
                </li>
                <li className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-[#4CD964] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-white/90">Materiali certificati di alta qualità</span>
                </li>
                <li className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-[#4CD964] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-white/90">Installazione professionale garantita</span>
                </li>
                <li className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-[#4CD964] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-white/90">Assistenza post-vendita dedicata</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-[#FAFAFA] rounded-3xl p-8 md:p-10">
            <h3 className="text-xl font-bold text-[#0B1E36] mb-6">Inviaci un Messaggio</h3>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              {/* Honeypot */}
              <input
                type="text"
                name="hp"
                value={formData.hp}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 0, height: 0, opacity: 0 }}
              />
              <div className="space-y-2">
                <Label htmlFor="contact-nome" className="text-[#0B1E36] font-medium">Nome *</Label>
                <Input
                  id="contact-nome"
                  name="nome"
                  type="text"
                  required
                  value={formData.nome}
                  onChange={handleChange}
                  placeholder="Il tuo nome"
                  className="h-12 border-[#E2E8F0] focus:border-[#0B1E36] focus:ring-[#0B1E36]/10 rounded-xl bg-white"
                  data-testid="contact-nome-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="contact-email" className="text-[#0B1E36] font-medium">Email *</Label>
                  <Input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@esempio.com"
                    className="h-12 border-[#E2E8F0] focus:border-[#0B1E36] focus:ring-[#0B1E36]/10 rounded-xl bg-white"
                    data-testid="contact-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-telefono" className="text-[#0B1E36] font-medium">Telefono *</Label>
                  <Input
                    id="contact-telefono"
                    name="telefono"
                    type="tel"
                    required
                    value={formData.telefono}
                    onChange={handleChange}
                    placeholder="+39 333 1234567"
                    className="h-12 border-[#E2E8F0] focus:border-[#0B1E36] focus:ring-[#0B1E36]/10 rounded-xl bg-white"
                    data-testid="contact-telefono-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-messaggio" className="text-[#0B1E36] font-medium">Messaggio *</Label>
                <Textarea
                  id="contact-messaggio"
                  name="messaggio"
                  required
                  value={formData.messaggio}
                  onChange={handleChange}
                  placeholder="Come possiamo aiutarti?"
                  className="min-h-[120px] border-[#E2E8F0] focus:border-[#0B1E36] focus:ring-[#0B1E36]/10 rounded-xl resize-none bg-white"
                  data-testid="contact-messaggio-input"
                />
              </div>

              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-shine bg-[#0B1E36] hover:bg-[#0B1E36]/90 text-white font-semibold h-14 rounded-full text-base flex items-center justify-center gap-2"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Invio in corso...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Invia Messaggio
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
